import { createSlice, createSelector, PayloadAction } from "@reduxjs/toolkit";
import {
    getShiftsByDate,
    getSwapEmployees,
    updateSwapRequest,
    deleteSwapRequest,
    getSwapEmployeesV2,
    getMySchedules,
    getMyScheduleDetails,
    getMyTodaysSchedule,
    getShiftsByDateV2,
} from "./ScheduleApi";
import { FULFILLED, PENDING, REJECTED } from "../../constants";

import { IShift } from "./models/schedule";
import { SwappableEmployee } from "../../../types/state/swap";
import { ScheduleStateType, ScheduleV2 } from "../../../types";
// import { IShift, IAvailShifts } from './models/schedule'

const initialState: ScheduleStateType = {
    requestState: FULFILLED,
    shifts: [],
    selectedShift: null,
    dateRange: null,
    availShifts: null,
    availEmp: [],
    txnsForManager: [],
    txnsForUser: [],
    /** trade swap */
    createSwapReason: "",
    swappableEmployees: [],
    swapFilterStartDate: "",
    swapFilterEndDate: "",
    selectedSwapEmployees: [],
    swapNote: "",
    actionConsecutiveDays: 1,
    isLoadingSchedules: false,
    schedules: [],
    schedulesMap: {},

    isLoadingTodaysSchedule: false,
};

export const ScheduleSlice = createSlice({
    name: "schedule",
    initialState,
    reducers: {
        selectShift: (state, data) => {
            const shiftId = data.payload;
            const shift: any = state.shifts.find((shift) => shift.id.toString() === shiftId);
            state.selectedShift = shift;
        },
        setDateRange: (state, data) => {
            state.dateRange = data.payload;
        },
        tgShift: (state, data) => {
            let payload = data.payload;
            let idx = state.txnsForUser.findIndex((txn: any) => txn.id === payload);

            if (idx === -1) {
                return;
            } else {
                state.txnsForUser[idx].checked = true;
            }
        },
        clearTxns: (state) => {
            state.txnsForUser = [];
        },
        updateSelectedSwapEmployees: (state, data) => {
            state.selectedSwapEmployees = data.payload;
        },
        setFilterStartDate: (state, data) => {
            state.swapFilterStartDate = data.payload;
        },
        setFilterEndDate: (state, data) => {
            state.swapFilterEndDate = data.payload;
        },
        setSwapNote: (state, data) => {
            state.swapNote = data.payload;
        },

        setActionConsecutiveDays: (state, action: PayloadAction<number>) => {
            state.actionConsecutiveDays = action.payload;
        },

        setSelectedScheduleForCannotWork: (state, action: PayloadAction<ScheduleV2 | undefined>) => {
            state.selectedScheduleForCannotWork = action.payload;
        },

        clearSelectedScheduleForCannotWork: (state) => {
            state.selectedScheduleForCannotWork = undefined;
        },
    },
    extraReducers: {
        /** NEW API REDUCERS */
        [getSwapEmployeesV2.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [getSwapEmployeesV2.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.swappableEmployees = [...payload];
            }
            state.requestState = FULFILLED;
        },

        [getSwapEmployeesV2.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [getMySchedules.pending.type]: (state) => {
            state.isLoadingSchedules = true;
            state.schedules = [];
            state.schedulesMap = {};
        },

        [getMySchedules.rejected.type]: (state) => {
            state.isLoadingSchedules = false;
        },

        [getMySchedules.fulfilled.type]: (state, action: PayloadAction<ScheduleV2[]>) => {
            state.isLoadingSchedules = false;
            const schedules = action.payload;
            state.schedules = schedules;

            state.schedulesMap = schedules.reduce((acc, schedule) => {
                acc[schedule.shift_date!] = acc[schedule.shift_date!] || [];
                acc[schedule.shift_date!].push(schedule);
                return acc;
            }, state.schedulesMap);
        },

        [getMyTodaysSchedule.pending.type]: (state) => {
            state.isLoadingTodaysSchedule = true;
        },

        [getMyTodaysSchedule.rejected.type]: (state) => {
            state.isLoadingTodaysSchedule = false;
        },

        [getMyTodaysSchedule.fulfilled.type]: (state, action: PayloadAction<ScheduleV2 | undefined>) => {
            state.isLoadingTodaysSchedule = false;
            state.todaysSchedule = action.payload;
        },

        /** Get shifts by date reducers */
        [getShiftsByDate.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },
        [getShiftsByDate.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.shifts = [...payload.data];
            }
            state.requestState = FULFILLED;
        },
        [getShiftsByDate.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [getShiftsByDateV2.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },
        [getShiftsByDateV2.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.shifts = [...payload];
            }
            state.requestState = FULFILLED;
        },
        [getShiftsByDateV2.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        /** get swap employees recuders */
        [getSwapEmployees.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },
        [getSwapEmployees.fulfilled.type]: (state, { payload }) => {
            if (payload) {
                state.swappableEmployees = [...payload.data];
            }
            state.requestState = FULFILLED;
        },
        [getSwapEmployees.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        /** update swap request reducers */
        [updateSwapRequest.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [updateSwapRequest.fulfilled.type]: (state, { payload }) => {
            state.requestState = FULFILLED;
        },

        [updateSwapRequest.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [deleteSwapRequest.pending.type]: (state, action) => {
            state.requestState = PENDING;
        },

        [deleteSwapRequest.fulfilled.type]: (state, { payload }) => {
            state.requestState = FULFILLED;
        },

        [deleteSwapRequest.rejected.type]: (state, action) => {
            state.requestState = REJECTED;
        },

        [getMyScheduleDetails.pending.type]: (state) => {
            state.isScheduleDetailLoading = true;
            state.scheduleDetail = undefined;
        },

        [getMyScheduleDetails.fulfilled.type]: (state, action: PayloadAction<ScheduleV2>) => {
            state.isScheduleDetailLoading = false;
            state.scheduleDetail = action.payload;
        },

        [getMyScheduleDetails.rejected.type]: (state) => {
            state.isScheduleDetailLoading = false;
        },
    },
});

export const selectShifts = createSelector(
    (state: any) => state.shifts,
    (shifts: any) => shifts
);

export const {
    selectShift,
    setDateRange,
    tgShift,
    clearTxns,
    updateSelectedSwapEmployees,
    setFilterStartDate,
    setFilterEndDate,
    setSwapNote,
    setActionConsecutiveDays,
    clearSelectedScheduleForCannotWork,
    setSelectedScheduleForCannotWork,
} = ScheduleSlice.actions;

export default ScheduleSlice.reducer;
